import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { JOB_ROLES } from "@/lib/api";
import { JOB_FIELDS } from "@/lib/schemas";
import DynamicField from "@/components/DynamicField";
import { toast } from "sonner";
import { Briefcase, CheckCircle, ClockCounterClockwise, XCircle } from "@phosphor-icons/react";

const STATUS_STYLE = {
  pending: { color: "bg-yellow-100 border-yellow-400 text-yellow-900", icon: <ClockCounterClockwise size={16} /> },
  approved: { color: "bg-emerald-100 border-emerald-500 text-emerald-900", icon: <CheckCircle size={16} weight="fill" /> },
  rejected: { color: "bg-red-100 border-red-500 text-red-900", icon: <XCircle size={16} weight="fill" /> },
  completed: { color: "bg-blue-100 border-blue-500 text-blue-900", icon: <CheckCircle size={16} weight="fill" /> },
  cancelled: { color: "bg-neutral-100 border-neutral-400 text-neutral-900", icon: <XCircle size={16} /> },
};

export default function StaffDashboard() {
  const { user, ready } = useAuth();
  const nav = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showApply, setShowApply] = useState(false);
  const [newRole, setNewRole] = useState("");
  const [details, setDetails] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (ready) {
      if (!user) nav("/login", { state: { from: "/staff" } });
      else if (user.role === "admin") nav("/admin");
      else if (user.role === "customer") nav("/dashboard");
    }
  }, [ready, user, nav]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/staff/me/applications");
      setApps(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user?.role === "staff") load(); }, [user]);

  const setDetail = (k, v) => setDetails((d) => ({ ...d, [k]: v }));

  const submitApply = async (e) => {
    e.preventDefault();
    if (!newRole) {
      toast.error("Pick a role first.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/staff/apply", { role: newRole, details });
      toast.success("Application submitted");
      setShowApply(false);
      setNewRole("");
      setDetails({});
      load();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not submit application";
      toast.error(typeof msg === "string" ? msg : "Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user || user.role !== "staff") return null;

  const total = apps.length;
  const pending = apps.filter((a) => a.status === "pending").length;
  const approved = apps.filter((a) => a.status === "approved").length;
  const primary = apps[0];

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12" data-testid="staff-dashboard">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <p className="label-eyebrow">Staff dashboard</p>
          <h1 className="font-display text-4xl md:text-6xl font-black tracking-tighter leading-none mt-3">
            Welcome, {user.name?.split(" ")[0]}.
          </h1>
          <p className="text-black/60 mt-3 text-sm">Track your applications and apply for additional roles.</p>
        </div>
        <button onClick={() => setShowApply((s) => !s)} className="btn-primary" data-testid="staff-apply-toggle">
          {showApply ? "Cancel" : "Apply for another role"}
        </button>
      </div>

      {/* Profile + stats */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-10">
        <div className="md:col-span-6 border border-black p-6 flex items-start gap-4" data-testid="staff-profile">
          <Briefcase size={28} weight="duotone" />
          <div className="flex-1">
            <p className="label-eyebrow text-black/50">Profile</p>
            <p className="font-display text-2xl font-black mt-2">{user.name}</p>
            <p className="text-sm text-black/60 mt-1">{user.email}{user.phone ? ` · ${user.phone}` : ""}</p>
            <p className="text-xs font-mono text-black/40 mt-2">
              Primary role: {(user.staff_role || primary?.role || "—").replace(/_/g, " ").toUpperCase()}
            </p>
          </div>
        </div>
        <Stat label="Applications" value={total} testid="staff-stat-total" className="md:col-span-2" />
        <Stat label="Pending" value={pending} testid="staff-stat-pending" className="md:col-span-2" />
        <Stat label="Approved" value={approved} testid="staff-stat-approved" className="md:col-span-2" />
      </div>

      {/* Apply form */}
      {showApply && (
        <form onSubmit={submitApply} className="border border-black p-6 mb-10" data-testid="staff-apply-form">
          <p className="label-eyebrow">Apply for another role</p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            {JOB_ROLES.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => setNewRole(r.key)}
                data-testid={`staff-newrole-${r.key}`}
                className={`text-left border p-3 transition-colors ${newRole === r.key ? "bg-black text-white border-black" : "bg-white text-black border-black/20 hover:border-black"}`}
              >
                <p className="font-display font-bold text-sm">{r.name}</p>
                <p className="font-mono text-[10px] opacity-60">{r.section}</p>
              </button>
            ))}
          </div>
          {newRole && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
              {(JOB_FIELDS[newRole] || []).map((f) => (
                <div key={f.name} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                  <DynamicField field={f} value={details[f.name]} onChange={setDetail} context={`staff-apply-${newRole}`} />
                </div>
              ))}
              <div className="md:col-span-2">
                <button disabled={submitting} className="btn-primary" data-testid="staff-apply-submit">
                  {submitting ? "Submitting…" : "Submit application"}
                </button>
              </div>
            </div>
          )}
        </form>
      )}

      {/* Applications */}
      <h2 className="font-display text-2xl font-black tracking-tight mt-4 mb-4">My applications</h2>
      {loading ? (
        <p className="font-mono text-xs">Loading…</p>
      ) : apps.length === 0 ? (
        <div className="border border-dashed border-black/30 p-12 text-center">
          <p className="text-black/60">No applications on file. Use "Apply for another role" above to start.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {apps.map((a) => {
            const meta = JOB_ROLES.find((r) => r.key === a.role);
            const s = STATUS_STYLE[a.status] || STATUS_STYLE.pending;
            return (
              <article key={a.id} className="border border-black p-6" data-testid={`staff-app-${a.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-mono text-[10px] text-black/50">{meta?.section?.toUpperCase() || a.role.toUpperCase()}</p>
                    <h3 className="font-display text-2xl font-black tracking-tight mt-1">
                      {meta?.name || a.role.replace(/_/g, " ")}
                    </h3>
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs border ${s.color}`} data-testid={`staff-app-status-${a.id}`}>
                    {s.icon} {a.status}
                  </span>
                </div>
                <dl className="mt-5 text-sm grid grid-cols-2 gap-y-2">
                  <dt className="text-black/50">Submitted</dt>
                  <dd className="font-mono text-xs">{a.created_at?.slice(0, 10)}</dd>
                  {a.details?.specialty && (<><dt className="text-black/50">Specialty</dt><dd>{a.details.specialty}</dd></>)}
                  {a.details?.experience && (<><dt className="text-black/50">Experience</dt><dd className="truncate">{a.details.experience}</dd></>)}
                  {a.details?.experience_years && (<><dt className="text-black/50">Years</dt><dd>{a.details.experience_years}</dd></>)}
                </dl>
              </article>
            );
          })}
        </div>
      )}

      <div className="mt-16">
        <Link to="/jobs" className="btn-outline" data-testid="staff-view-roles">See all open roles</Link>
      </div>
    </div>
  );
}

function Stat({ label, value, testid, className = "" }) {
  return (
    <div className={`border border-black p-5 ${className}`} data-testid={testid}>
      <p className="label-eyebrow text-black/50">{label}</p>
      <p className="font-display text-3xl font-black mt-2">{value}</p>
    </div>
  );
}
